/**
 * Socket.IO 싱글턴 + 룸 구독 훅.
 *
 * 🔴 진행 상황은 폴링하지 않습니다. 서버가 push 한 것만 받습니다.
 *    재접속 직후 놓친 구간만 `GET /api/jobs/{id}/events?after_id=` 로 따라잡습니다.
 *
 * 룸: 전역 `all`, 작업별 `job:{id}`.
 * 클라이언트 → 서버: `subscribe` / `unsubscribe` (payload `{ room }`).
 */

import { useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'

import type { ServerEventName, ServerEvents } from '@/types/api'

/** 서버 → 클라이언트 (docs/API.md 「Socket.IO 이벤트」). */
type ServerToClientEvents = {
  [K in ServerEventName]: (payload: ServerEvents[K]) => void
}

/** 클라이언트 → 서버. 룸 구독이 전부입니다. */
interface ClientToServerEvents {
  subscribe: (payload: { room: string }) => void
  unsubscribe: (payload: { room: string }) => void
}

export type VoxctxSocket = Socket<ServerToClientEvents, ClientToServerEvents>

/** 전역 룸 — 어떤 화면에서든 새 작업·비용 발생을 알아야 할 때. */
export const ROOM_ALL = 'all'

/** 작업 상세 룸 이름을 만듭니다. */
export function jobRoom(jobId: number): string {
  return `job:${jobId}`
}

let socket: VoxctxSocket | null = null

/** 룸별 구독자 수. 한 화면에서 같은 룸을 두 곳이 보고 있어도 안전하게 해제됩니다. */
const roomRefs = new Map<string, number>()

export function getSocket(): VoxctxSocket {
  if (socket) return socket

  // URL 을 주지 않으면 현재 오리진에 붙습니다. 개발 중에는 Vite 가
  // /socket.io 를 127.0.0.1:33252 로 프록시합니다 (vite.config.ts).
  // 🔴 polling 을 먼저 씁니다 (2026-08-10 실측).
  //
  //   개발 서버(Werkzeug)는 WebSocket 업그레이드를 지원하지 않아 `/socket.io/
  //   ?transport=websocket` 에 **400** 을 돌려줍니다. websocket 을 앞에 두면
  //   매번 실패로 시작해 화면에 "연결이 끊겼습니다" 가 뜹니다.
  //
  //   polling 을 먼저 두면 즉시 붙고, 서버가 WebSocket 을 지원하는 환경에서는
  //   Socket.IO 가 알아서 업그레이드합니다. 여기서 말하는 polling 은 Socket.IO
  //   의 long-polling 이라 **서버 push 의미론이 그대로 유지됩니다** — 클라이언트가
  //   상태를 주기적으로 조회하는 그 폴링이 아닙니다.
  socket = io({
    path: '/socket.io',
    transports: ['polling', 'websocket'],
    upgrade: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
  })

  // 끊겼다 붙으면 서버는 우리를 기억하지 못합니다. 보고 있던 룸을 다시 신청합니다.
  socket.on('connect', () => {
    for (const [room, count] of roomRefs) {
      if (count > 0) socket?.emit('subscribe', { room })
    }
  })

  return socket
}

export function joinRoom(room: string): void {
  const next = (roomRefs.get(room) ?? 0) + 1
  roomRefs.set(room, next)
  if (next === 1) {
    const s = getSocket()
    if (s.connected) s.emit('subscribe', { room })
  }
}

export function leaveRoom(room: string): void {
  const next = (roomRefs.get(room) ?? 1) - 1
  if (next <= 0) {
    roomRefs.delete(room)
    const s = getSocket()
    if (s.connected) s.emit('unsubscribe', { room })
  } else {
    roomRefs.set(room, next)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 훅
// ─────────────────────────────────────────────────────────────────────────────

export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected'

/**
 * 실시간 연결 상태.
 *
 * 🔴 끊긴 상태를 조용히 숨기지 않습니다. 화면이 멈춘 것인지 서버가 조용한
 *    것인지 구별되지 않으면, 사용자는 끝난 작업을 계속 기다리게 됩니다.
 */
export function useConnectionStatus(): ConnectionStatus {
  const [status, setStatus] = useState<ConnectionStatus>(() =>
    getSocket().connected ? 'connected' : 'connecting',
  )

  useEffect(() => {
    const s = getSocket()
    const onConnect = () => setStatus('connected')
    const onDisconnect = () => setStatus('disconnected')
    const onReconnecting = () => setStatus('connecting')

    s.on('connect', onConnect)
    s.on('disconnect', onDisconnect)
    s.io.on('reconnect_attempt', onReconnecting)
    s.io.on('error', onDisconnect)

    setStatus(s.connected ? 'connected' : 'connecting')

    return () => {
      s.off('connect', onConnect)
      s.off('disconnect', onDisconnect)
      s.io.off('reconnect_attempt', onReconnecting)
      s.io.off('error', onDisconnect)
    }
  }, [])

  return status
}

/** 룸을 구독합니다. `null` 을 주면 아무것도 구독하지 않습니다. */
export function useRoom(room: string | null): void {
  useEffect(() => {
    if (!room) return
    joinRoom(room)
    return () => leaveRoom(room)
  }, [room])
}

/**
 * 서버 이벤트를 듣습니다.
 *
 * 핸들러는 ref 로 잡아 두므로, 매 렌더마다 새 함수를 넘겨도 재구독하지 않습니다.
 * (재구독이 일어나면 그 찰나에 도착한 이벤트를 놓칩니다.)
 */
export function useSocketEvent<K extends ServerEventName>(
  event: K,
  handler: (payload: ServerEvents[K]) => void,
  enabled = true,
): void {
  const ref = useRef(handler)
  ref.current = handler

  useEffect(() => {
    if (!enabled) return
    const s = getSocket()
    const listener = (payload: ServerEvents[K]) => ref.current(payload)

    // socket.io 의 `on`/`off` 는 이벤트 이름이 제네릭(K)으로 남아 있으면
    // 리스너 타입을 조건부 타입인 채로 미뤄 두어 대입을 거절합니다
    // (TypeScript 의 지연 조건부 타입 한계입니다). 이 훅의 **공개 시그니처는
    // 그대로 완전히 타입이 붙어 있고**, 등록 지점에서만 좁혀 줍니다.
    type Bridge = {
      on(ev: K, fn: typeof listener): void
      off(ev: K, fn: typeof listener): void
    }
    const bridge = s as unknown as Bridge

    bridge.on(event, listener)
    return () => {
      bridge.off(event, listener)
    }
  }, [event, enabled])
}

/** 특정 작업의 룸을 구독하면서 이벤트도 함께 듣는 조합 훅. */
export function useJobChannel<K extends ServerEventName>(
  jobId: number | null,
  event: K,
  handler: (payload: ServerEvents[K]) => void,
): void {
  useRoom(jobId === null ? null : jobRoom(jobId))
  useSocketEvent(event, handler, jobId !== null)
}
